import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { driverName, findBooking, findDriver, findListing, listingName } from "../../lib/company";
import { useCompany } from "../../lib/companyContext";
import { useBookingApi } from "../../components/company/useBookingApi";
import { cn, shortDate, shortDateTime } from "../../lib/format";
import { MessageBubble, MessageInput, PageTitle } from "../../components/company/ui";
import { useT } from "../../i18n";

/** Conversation list + thread; on mobile only one of the two is shown. */
export default function CompanyMessages() {
  const t = useT();
  const copy = t.company.messages;
  const { id } = useParams();
  const { state, toast } = useCompany();
  const api = useBookingApi();
  const [text, setText] = useState("");

  // Opening a thread marks the driver's messages as read
  useEffect(() => {
    if (id) void api.open(id);
  }, [api, id]);

  const threads = state.bookings
    .filter((b) => b.messages.length)
    .sort((a, b) => b.messages.at(-1)!.at.localeCompare(a.messages.at(-1)!.at));
  const selected = id ? findBooking(state, id) : undefined;
  const selectedDriver = selected && findDriver(state, selected.driverId);

  return (
    <>
      <PageTitle>{copy.title}</PageTitle>
      <div className="grid min-h-105 overflow-hidden rounded-card bg-white lg:grid-cols-[300px_minmax(0,1fr)]">
        <div className={cn("border-line lg:block lg:border-r", selected && "hidden")}>
          {threads.map((t) => {
            const last = t.messages.at(-1)!;
            return (
              <Link
                key={t.id}
                to={`/company/messages/${t.id}`}
                className={cn(
                  "block border-b border-[#F0F0F0] px-4 py-3.5 text-ink no-underline",
                  selected?.id === t.id ? "bg-surface" : "bg-white hover:bg-[#FAFAFA]",
                )}
              >
                <div className="flex justify-between gap-2">
                  <b className="flex items-center gap-1.5 text-sm font-medium">
                    {driverName(findDriver(state, t.driverId))}
                    {!!t.unread && selected?.id !== t.id && (
                      <span className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-brand px-1 text-[10px] text-white">
                        {t.unread}
                      </span>
                    )}
                  </b>
                  <span className="text-[11px] text-faint">{shortDate(last.at)}</span>
                </div>
                <div className="mt-0.5 text-xs text-muted">
                  {findListing(state, t.listingId) ? `${listingName(findListing(state, t.listingId)!)} · ` : ""}
                  {t.id}
                </div>
                <div className="mt-1 truncate text-[13px] text-ink-soft">{last.text}</div>
              </Link>
            );
          })}
          {threads.length === 0 && <p className="m-0 p-6 text-sm text-faint">{copy.empty}</p>}
        </div>

        {selected && selectedDriver ? (
          <div className="flex flex-col">
            <div className="flex items-center gap-2.5 border-b border-line px-4.5 py-3.5">
              <Link to="/company/messages" className="text-brand no-underline lg:hidden" aria-label={t.common.back}>
                ←
              </Link>
              <b className="font-medium">{driverName(selectedDriver)}</b>
              <Link to={`/company/bookings/${selected.id}`} className="ml-auto text-[13px] text-brand hover:text-brand-link">
                {selected.id}
              </Link>
            </div>
            <div className="grid flex-1 content-start gap-2 px-4.5 py-4">
              {selected.messages.map((m, i) => (
                <MessageBubble
                  key={i}
                  from={m.from}
                  text={m.text}
                  meta={`${m.from === "company" ? t.company.booking.you : m.from === "driver" ? driverName(selectedDriver) : t.company.booking.platform} · ${shortDateTime(m.at)}`}
                />
              ))}
            </div>
            <div className="border-t border-line px-4.5 py-3">
              <MessageInput
                value={text}
                onChange={setText}
                placeholder={copy.placeholder}
                onSend={async () => {
                  const message = text.trim();
                  if (!message) return;
                  setText("");
                  const error = await api.send(selected.id, message);
                  if (error) {
                    toast.error(error);
                    setText(message);
                  }
                }}
              />
            </div>
          </div>
        ) : (
          <div className="hidden items-center justify-center text-sm text-faint lg:flex">
            {copy.select}
          </div>
        )}
      </div>
    </>
  );
}
